<script setup lang="ts">
import { computed } from 'vue';

import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type { AppSingleSelectType } from 'libs/ui/src/lib/components/selects/models/single.model';

import { AppSingleSelect, AppSvgIcon } from '@libs/ui';
import type {
  AppSingleSelectOption,
  AppSingleSelectFontVariant,
} from '@libs/ui';

import { getSelectedOptionForSuggestionType } from './SuggestionTypeSelector.helper';
import {
  SuggestionType,
  InfoIconPosition,
  SuggestionTypeSelectorSize,
} from './SuggestionTypeSelector.model';

const { t } = i18n.global;
interface SuggestionTypeSelectorProps {
  suggestionType: SuggestionType;
  fontVariant: AppSingleSelectFontVariant;
  appSelectType: AppSingleSelectType;
  iconPosition: 'right' | 'left';
  showInfoIcon: InfoIconPosition;
  size: SuggestionTypeSelectorSize;
  isDirectFlightCheckboxExperiment?: boolean;
}
const props = defineProps<SuggestionTypeSelectorProps>();

interface SuggestionTypeSelectorEmits {
  (eventName: 'suggestion-type-selected', suggestionType: SuggestionType): void;
  (eventName: 'show-info'): void;
}

const emit = defineEmits<SuggestionTypeSelectorEmits>();

const selectedSuggestionType = computed(() =>
  getSelectedOptionForSuggestionType(
    SUGGESTION_TYPE_OPTIONS,
    props.suggestionType
  )
);

function onSuggestionTypeSelect(option: AppSingleSelectOption<string>): void {
  emit('suggestion-type-selected', option.id as SuggestionType);
}

const SUGGESTION_TYPE_OPTIONS: AppSingleSelectOption<string>[] = [
  {
    id: SuggestionType.Cheapest,
    label: t('suggestions.cheapest'),
  },
  {
    id: SuggestionType.MostPopular,
    label: t('suggestions.popular'),
  },
];

const insideIcon = computed(() => {
  if (!props.isDirectFlightCheckboxExperiment) {
    return props.showInfoIcon === InfoIconPosition.Inside &&
      selectedSuggestionType.value?.id === SuggestionType.MostPopular
      ? 'infoCircle'
      : undefined;
  } else {
    return 'list';
  }
});
</script>

<template>
  <div class="suggestion-type-selector" :class="[`${props.iconPosition}-icon`]">
    <AppSvgIcon
      v-if="
        selectedSuggestionType?.id === SuggestionType.MostPopular &&
        props.showInfoIcon === InfoIconPosition.Outside
      "
      class="info-icon"
      name="infoCircle"
      size="18"
      @click="emit('show-info')"
    />
    <AppSingleSelect
      :type="props.appSelectType"
      :font-variant="props.fontVariant"
      :options="SUGGESTION_TYPE_OPTIONS"
      :size="
        props.size !== SuggestionTypeSelectorSize.Normal
          ? props.size
          : undefined
      "
      :selected="selectedSuggestionType"
      :left-side-icon="insideIcon"
      @select="onSuggestionTypeSelect"
      @left-icon-click="emit('show-info')"
    />
  </div>
</template>

<style scoped lang="scss">
.suggestion-type-selector {
  display: flex;
  align-items: center;
  gap: var(--spacing-8);
  &.right-icon {
    flex-direction: row;
  }
  &.left-icon {
    flex-direction: row-reverse;
  }
  .info-icon {
    cursor: pointer;
  }
}
</style>
